import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { CountrySelect } from "../../../components/forms/CountrySelect";

const EMPTY = {
  company: "",
  street: "",
  city: "",
  postal_code: "",
  country: "DE",
  vat_id: "",
  contact_phone: "",
  contact_email: "",
};

/** Client-side validation — mirrors the backend rules.
 *
 * A12: `vat_id` is OPTIONAL. Empty strings are normalised to `null` for
 * the POST body. `is_default` is retired (no per-user default concept in
 * a global address pool).
 */
function validate(values, t) {
  const errors = {};
  const req = t("profile.address_form.required");

  if ((values.company || "").trim().length < 2)
    errors.company = t("profile.address_form.err_company");
  if (!(values.street || "").trim()) errors.street = req;
  if ((values.postal_code || "").trim().length < 4)
    errors.postal_code = t("profile.address_form.err_postal");
  if (!(values.city || "").trim()) errors.city = req;
  if (((values.country || "").trim().toUpperCase()).length !== 2)
    errors.country = t("profile.address_form.err_country");

  // A18.1 — contact_phone / contact_email are no longer surfaced on the
  // admin address form. Backend keeps them Optional; we pass whatever
  // was already on the record through unchanged.

  const rawVat = (values.vat_id || "").trim();
  const cleaned = {
    company: (values.company || "").trim(),
    street: (values.street || "").trim(),
    city: (values.city || "").trim(),
    postal_code: (values.postal_code || "").trim(),
    country: (values.country || "").trim().toUpperCase(),
    vat_id: rawVat || null,
    // Preserve any existing phone/email on the record (edit flow) so
    // the form doesn't wipe them by omission.
    contact_phone: (values.contact_phone || "").trim() || null,
    contact_email: (values.contact_email || "").trim() || null,
  };
  return { errors, isValid: Object.keys(errors).length === 0, cleaned };
}

export function AddressFormModal({
  open,
  onOpenChange,
  initial,
  onSubmit,
  submitting,
  headerAccessory,
}) {
  const { t } = useTranslation();
  const [values, setValues] = useState(EMPTY);
  const [touched, setTouched] = useState({});

  useEffect(() => {
    if (open) {
      setValues(initial ? { ...EMPTY, ...initial } : EMPTY);
      setTouched({});
    }
  }, [open, initial]);

  const set = (k) => (e) =>
    setValues((v) => ({
      ...v,
      [k]: e && e.target ? e.target.value : e,
    }));

  const markTouched = (k) => () =>
    setTouched((tt) => (tt[k] ? tt : { ...tt, [k]: true }));

  const live = useMemo(() => validate(values, t), [values, t]);

  const submit = (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!live.isValid) {
      setTouched({
        company: true,
        street: true,
        city: true,
        postal_code: true,
        country: true,
      });
      return;
    }
    onSubmit(live.cleaned);
  };

  const isEdit = Boolean(initial?.id);
  const canSubmit = live.isValid && !submitting;
  const errorFor = (k) => (touched[k] ? live.errors[k] : null);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="address-form-modal"
        className="w-full max-w-3xl mx-4 sm:mx-auto border-border-subtle bg-surface text-text-primary p-6 sm:p-8 [&_button.absolute]:text-text-secondary max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle data-testid="address-form-title" className="text-xl">
            {isEdit
              ? t("profile.address_form.edit_title")
              : t("profile.address_form.add_title")}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {isEdit
              ? t("profile.address_form.edit_title")
              : t("profile.address_form.add_title")}
          </DialogDescription>
        </DialogHeader>

        {headerAccessory}

        <form onSubmit={submit} className="space-y-6" noValidate>
          {/* Row 1: Company */}
          <Field
            label={t("profile.address_form.company")}
            required
            error={errorFor("company")}
          >
            <Input
              value={values.company}
              onChange={set("company")}
              onBlur={markTouched("company")}
              data-testid="af-company"
              autoFocus
              className="h-11 bg-surface border-border-subtle focus-visible:ring-champagne text-base"
            />
          </Field>

          {/* Row 2: Street */}
          <Field
            label={t("profile.address_form.street")}
            required
            error={errorFor("street")}
          >
            <Input
              value={values.street}
              onChange={set("street")}
              onBlur={markTouched("street")}
              data-testid="af-street"
              className="h-10 bg-surface border-border-subtle focus-visible:ring-champagne"
            />
          </Field>

          {/* Row 3: Postal + City */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Field
              label={t("profile.address_form.postal_code")}
              required
              error={errorFor("postal_code")}
            >
              <Input
                value={values.postal_code}
                onChange={set("postal_code")}
                onBlur={markTouched("postal_code")}
                data-testid="af-postal"
                className="h-10 bg-surface border-border-subtle focus-visible:ring-champagne"
              />
            </Field>
            <Field
              label={t("profile.address_form.city")}
              required
              error={errorFor("city")}
              className="sm:col-span-2"
            >
              <Input
                value={values.city}
                onChange={set("city")}
                onBlur={markTouched("city")}
                data-testid="af-city"
                className="h-10 bg-surface border-border-subtle focus-visible:ring-champagne"
              />
            </Field>
          </div>

          {/* Row 4: Country + VAT */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Field
              label={t("profile.address_form.country")}
              required
              error={errorFor("country")}
            >
              <CountrySelect
                value={values.country}
                onChange={(iso2) => {
                  setValues((v) => ({ ...v, country: iso2 }));
                  markTouched("country")();
                }}
                testIdRoot="af-country"
                error={Boolean(errorFor("country"))}
              />
            </Field>
            <Field
              label={t("profile.address_form.vat_id")}
              error={errorFor("vat_id")}
              className="sm:col-span-2"
            >
              <Input
                value={values.vat_id}
                onChange={set("vat_id")}
                onBlur={markTouched("vat_id")}
                data-testid="af-vat"
                className="h-10 bg-surface border-border-subtle focus-visible:ring-champagne"
              />
            </Field>
          </div>

          {/* Row 5: (A18.1) phone + email removed from the admin
              address form. Backend keeps both fields Optional; existing
              records still round-trip via the cleaned payload above. */}

          <DialogFooter className="pt-2 sm:justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              data-testid="af-cancel"
              className="border-border-subtle text-text-primary hover:bg-offwhite"
            >
              {t("common.cancel")}
            </Button>
            <Button
              type="submit"
              disabled={!canSubmit}
              data-testid="af-submit"
              className="bg-charcoal text-white hover:bg-charcoal-soft focus-visible:ring-champagne"
            >
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t("common.saving")}
                </>
              ) : (
                t("common.save")
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, required, error, className = "", children }) {
  return (
    <div className={`space-y-1.5 ${className}`}>
      <Label className="text-xs font-medium uppercase tracking-wide text-text-secondary">
        {label}
        {required && <span className="ml-0.5 text-status-unavailable">*</span>}
      </Label>
      {children}
      {error && <p className="text-xs text-status-unavailable">{error}</p>}
    </div>
  );
}
